/**
 * "Load more" for a cursor-paged list that also filters.
 *
 * Pages already fetched are kept so the list grows as the admin loads more.
 * The moment the filters change (`resetKey`) the pager starts over, in the same
 * render: a cursor issued for the old filters would otherwise be sent with the
 * new ones for one request.
 */

import { useState } from "react";

interface PagerState<T> {
  key: string;
  cursor: string | undefined;
  previous: T[];
}

export function useLoadMore<T>(resetKey: string) {
  const [state, setState] = useState<PagerState<T>>({
    key: resetKey,
    cursor: undefined,
    previous: [],
  });
  const live: PagerState<T> =
    state.key === resetKey
      ? state
      : { key: resetKey, cursor: undefined, previous: [] };

  return {
    cursor: live.cursor,
    previous: live.previous,
    /** Keep the rows now showing and ask for the page after them. */
    loadMore: (showing: readonly T[], nextCursor: string | undefined) =>
      setState({
        key: resetKey,
        cursor: nextCursor,
        previous: [...live.previous, ...showing],
      }),
  };
}
